import { useCallback } from 'react';
import { HoverEvents, PressEvents } from 'react-aria';

import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { TextAreaProps, TextInputBaseProps } from '../TextInput/types';
import { cn } from '../../lib/css/className/className';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { ButtonBase, ButtonBaseProps } from '../ButtonBase';
import type { FormFieldControlProps } from '../FormField';
import { HStack } from '../Stack';
import { ClearButton } from '../TextInput/ClearButton';
import { getColorSkinClass } from '../TextInput/utils';
import { useTheme } from '../../lib/theme';
import { TestProps } from '../../internal/types/testTypes';

import './PseudoInput.css';

export interface PseudoInputProps
    extends PressEvents,
        HoverEvents,
        FormFieldControlProps,
        TestProps,
        Pick<
            TextInputBaseProps,
            | 'size'
            | 'color'
            | 'addonRight'
            | 'isDisabled'
            | 'placeholder'
            | 'value'
            | 'hasClearButton'
            | 'onClear'
            | 'onChange'
        >,
        Pick<TextAreaProps, 'isTextarea'> {}

const cls = cn('PseudoInput');

/**
 * Компонент, заменяющий TextInput в мобильном Combobox. Обрабатывает клик, рендерит крестик, placeholder, addonRight
 * Имеет те же размеры и цвета, что и TextInput.
 * Для использования внутри библиотеки компонентов.
 * @internal
 */
export function PseudoInput(props: PseudoInputProps) {
    const {
        size = 'm',
        color = 'contour',
        value,
        placeholder,
        isDisabled,
        validationState,
        addonRight,
        hasClearButton,
        onClear,
        onChange,
        isTextarea,
        'data-testid': dataTestId = 'PseudoInput',
    } = props;
    const { iconSize } = useTheme().sizeTokens[size];
    const onClearHandler = useCallback(() => {
        if (onClear) {
            onClear();
            return;
        }

        onChange?.('');
    }, [onClear, onChange]);

    const getClassName = useCallback<ButtonBaseProps['getClassName']>(
        ({ isDisabled, isHovered, isPressed }) => {
            const sizeSkin = getSizeSkin(size);
            const colorSkin = getColorSkinClass(color, {
                isDisabled: isDisabled ?? false,
                isHovered: (isHovered || isPressed) ?? false,
            });

            return cls({ hasError: !isDisabled && validationState === 'invalid' }, [sizeSkin, colorSkin]);
        },
        [color, size, validationState],
    );

    let content = (
        <TextGenericWithSkin
            className={cls('text')}
            skinSize={size}
            textSize="current-body"
            color="current"
            role={value ? 'main' : 'placeholder'}
            isTruncated={!isTextarea}
            data-testid={`${dataTestId}`}
        >
            {value || placeholder}
        </TextGenericWithSkin>
    );

    const rightPart =
        addonRight || hasClearButton ? (
            <HStack gap="8">
                {addonRight}
                {hasClearButton && Boolean(value) && (
                    <ClearButton
                        onClick={onClearHandler}
                        isDisabled={isDisabled}
                        size={iconSize}
                        data-testid={`${dataTestId}`}
                    />
                )}
            </HStack>
        ) : null;

    if (rightPart) {
        content = (
            <HStack justifyContent="space-between" alignItems="flex-start">
                {content}
                {rightPart}
            </HStack>
        );
    }

    return (
        <ButtonBase
            {...props}
            data-testid={`${dataTestId}.Button`}
            as="div"
            getClassName={getClassName}
            content={content}
        />
    );
}
